import React, { useEffect, useRef, useState } from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import MaximizeIcon from '@mui/icons-material/OpenInFull';
import { useSnackbar } from 'material-ui-snackbar-provider';
import Draggable from 'react-draggable';

import Modal from '../../modules/Modal';
import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import { bytesToMB, getVideoResolution } from '../../../helpers';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  postCancelAdditionalAngleUpload,
  postCompleteAdditionalAngleUpload,
  postCreateAdditionalAngleUpload,
  postRegeneratePresignedUrls,
  postUploadChunkByPresignedUrl,
} from '../../../api';
import { VIDEOFEED } from '../../../enums/TYPES';
import { UploadingStatus, updateUploadingContentState } from '../../../store/sharedStore/sharedStoreSlice';
import { ReactComponent as MinimizeIcon } from '../../../assets/icons/minimize.svg';
import {
  getMissingPartNumbers,
  getUploadedBytesForParts,
  hasEveryPart,
  normalizeCompletedParts,
  runWithConcurrency,
} from '../../../helpers/multipartUpload';
import styles from './AdditionalAnglesUploadModal.module.scss';

type TAdditionalAnglesUploadModal = {
  open: boolean;
  onClose: () => void;
  videofeed: VIDEOFEED;
  onSuccess?: () => void;
  minimizable?: boolean;
};

const PART_SIZE = 5 * 1024 * 1024;
const MAX_CONCURRENT_PARTS = 3;
const ADDITIONAL_ANGLE_RESUME_PREFIX = '=additionalAngleToResume=';

type AdditionalAngleResumeUpload = {
  storageKey: string;
  videofeedId: string;
  fileName: string;
  size: number;
  quality: number;
  duration?: number;
  numberOfParts: number;
  videoId: string;
  uploadId: string;
  chunks: { PartNumber: number; ETag: string }[];
  createdAt: number;
};

const isAllowedAngleFile = (file: File) => /\.(mp4|mov|m4v)$/i.test(file.name);

const getBaseName = (fileName: string) => fileName.replace(/\.(mp4|mov|m4v)$/i, '');

const getResumeUpload = (videofeedId: string, file?: File): AdditionalAngleResumeUpload | null => {
  const prefix = `${ADDITIONAL_ANGLE_RESUME_PREFIX}${videofeedId}::`;
  let latest: AdditionalAngleResumeUpload | null = null;

  for (let index = 0; index < localStorage.length; index++) {
    const storageKey = localStorage.key(index);
    if (!storageKey?.startsWith(prefix)) continue;

    try {
      const upload = JSON.parse(localStorage.getItem(storageKey) || '') as AdditionalAngleResumeUpload;
      if (
        upload?.uploadId &&
        (!file || (upload.fileName === file.name && Number(upload.size) === file.size)) &&
        (!latest || upload.createdAt > latest.createdAt)
      ) {
        latest = { ...upload, storageKey };
      }
    } catch {
      localStorage.removeItem(storageKey);
    }
  }

  return latest;
};

const saveResumeUpload = (upload: AdditionalAngleResumeUpload) => {
  localStorage.setItem(upload.storageKey, JSON.stringify(upload));
};

const AdditionalAnglesUploadModal: React.FC<TAdditionalAnglesUploadModal> = ({ open, onClose, videofeed, onSuccess, minimizable = false }) => {
  const notification = useSnackbar();
  const dispatch = useAppDispatch();
  const { uploadingContentState } = useAppSelector((state) => state.sharedStore);
  const inputRef = useRef<HTMLInputElement>(null);
  const uploadingContentStateRef = useRef(uploadingContentState);
  const uploadControllerRef = useRef<AbortController | null>(null);
  const activeUploadRef = useRef<AdditionalAngleResumeUpload | null>(null);
  const cancelRequestedRef = useRef(false);
  const minimizedWasDraggedRef = useRef(false);
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [resumeUpload, setResumeUpload] = useState<AdditionalAngleResumeUpload | null>(null);
  const [uploadError, setUploadError] = useState('');

  useEffect(() => {
    uploadingContentStateRef.current = uploadingContentState;
  }, [uploadingContentState]);

  const collectionFeeds = [videofeed, ...(videofeed?.reference_timelines || [])];
  const existingVideoCount = Math.max(
    0,
    ...collectionFeeds.map((feed) => feed?.videos?.length || 0),
  );
  const videoIds = new Set(
    collectionFeeds.flatMap((feed) => (feed?.videos || []).map((video) => String(video.id))),
  );
  const pendingAdditionalAngles = uploadingContentState.filter(
    (item) =>
      String(item?.videofeedId) === String(videofeed?.id) &&
      item?.uploadMode === 'additional-angle' &&
      item?.status !== UploadingStatus.FINISHED &&
      item?.status !== UploadingStatus.FAILED &&
      (!item.videoId || !videoIds.has(String(item.videoId))),
  );
  const remainingSlots = Math.max(
    0,
    4 - existingVideoCount - pendingAdditionalAngles.length,
  );

  useEffect(() => {
    if (open) {
      setResumeUpload(getResumeUpload(String(videofeed.id)));
    }
  }, [open, videofeed.id]);

  useEffect(() => {
    if (!open) {
      setFiles([]);
      setUploading(false);
      setProgress(0);
      setIsDragging(false);
      setMinimized(false);
      setUploadError('');
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  }, [open]);

  const addFiles = (incoming: File[]) => {
    if (uploading) {
      return;
    }

    if (!remainingSlots && !resumeUpload) {
      notification.showMessage('This videofeed already has 4 angles.');
      return;
    }

    const allowedFiles = incoming.filter(isAllowedAngleFile);
    if (!allowedFiles.length) {
      notification.showMessage('Only .mp4, .mov and .m4v files can be uploaded as additional angles.');
      return;
    }

    if (allowedFiles.length !== incoming.length) {
      notification.showMessage('Only .mp4, .mov and .m4v files can be uploaded as additional angles. Unsupported files were ignored.');
    }

    const hasResumeFile =
      !resumeUpload ||
      [...files, ...allowedFiles].some(
        (file) => file.name === resumeUpload.fileName && file.size === Number(resumeUpload.size),
      );
    if (!hasResumeFile) {
      notification.showMessage(`Select ${resumeUpload?.fileName} to resume the interrupted upload.`);
      return;
    }

    const resumeFile = resumeUpload
      ? allowedFiles.find((file) => file.name === resumeUpload.fileName && file.size === Number(resumeUpload.size))
      : undefined;
    const orderedFiles = resumeFile
      ? [resumeFile, ...allowedFiles.filter((file) => file !== resumeFile)]
      : allowedFiles;
    const deduped = orderedFiles.filter(
      (file) => !files.some((existing) => existing.name === file.name && existing.size === file.size),
    );

    if (!deduped.length) {
      return;
    }

    const nextFiles = [...files, ...deduped];
    const availableSlots = remainingSlots + (resumeUpload ? 1 : 0);
    if (nextFiles.length > availableSlots) {
      notification.showMessage(`You can add up to ${availableSlots} more angle(s) to this videofeed.`);
      return;
    }

    setFiles(nextFiles);
    setUploadError('');
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files || []);
    addFiles(selected);
    event.target.value = '';
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const dropped = Array.from(event.dataTransfer.files || []);
    addFiles(dropped);
  };

  const clearCancelledUpload = (upload: AdditionalAngleResumeUpload | null) => {
    if (upload) {
      localStorage.removeItem(upload.storageKey);
      const nextUploadingState = uploadingContentStateRef.current.filter(
        (item) => String(item.videoId) !== String(upload.videoId),
      );
      dispatch(updateUploadingContentState(nextUploadingState));
      uploadingContentStateRef.current = nextUploadingState;
    }
    activeUploadRef.current = null;
    setResumeUpload(null);
    setFiles([]);
    setProgress(0);
    setUploadError('');
    onSuccess?.();
  };

  const finishCancellation = async (upload: AdditionalAngleResumeUpload | null, closeModal = true) => {
    try {
      if (upload) await postCancelAdditionalAngleUpload(upload.uploadId);
      clearCancelledUpload(upload);
      if (closeModal) onClose();
      notification.showMessage(closeModal ? 'Additional angle upload cancelled' : 'Interrupted upload deleted');
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        (closeModal ? 'Failed to cancel additional angle upload' : 'Failed to delete interrupted upload');
      if (message === 'This upload can no longer be cancelled') {
        clearCancelledUpload(upload);
        if (closeModal) onClose();
      }
      notification.showMessage(message);
    } finally {
      setCancelling(false);
    }
  };

  const handleCancelUpload = () => {
    if (!uploading) {
      onClose();
      return;
    }
    if (cancelling) return;

    cancelRequestedRef.current = true;
    setCancelling(true);
    uploadControllerRef.current?.abort();
  };

  const throwIfCancelled = () => {
    if (cancelRequestedRef.current) throw new Error('Upload cancelled');
  };

  const handleUpload = async () => {
    if (!files.length || uploading) {
      return;
    }

    const uploadController = new AbortController();
    uploadControllerRef.current = uploadController;
    cancelRequestedRef.current = false;
    const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
    let uploadedBytes = 0;
    let currentUploadId = '';
    const baseVideofeedId = String(videofeed.id);
    activeUploadRef.current = getResumeUpload(baseVideofeedId, files[0]);

    const updatePlaceholder = (
      file: File,
      index: number,
      videoId: string,
      createdAt: number,
      status: UploadingStatus.UPLOADING | UploadingStatus.TRANSCODING,
    ) => {
      const nextUploadingState = [...uploadingContentStateRef.current];
      const placeholder = {
        id: `additional-angle-${baseVideofeedId}-${createdAt}-${index}`,
        videofeedId: baseVideofeedId,
        videofeedTitle: videofeed.title,
        status,
        size: file.size,
        name: `${status === UploadingStatus.UPLOADING ? 'Uploading' : 'Processing'} ${getBaseName(file.name)}`,
        originalName: file.name,
        uploadMode: 'additional-angle' as const,
        baseVideoCount: existingVideoCount,
        videoId,
        createdAt,
      };
      const existingIndex = nextUploadingState.findIndex(
        (item) => item.uploadMode === 'additional-angle' && String(item.videoId) === String(videoId),
      );
      if (existingIndex >= 0) {
        nextUploadingState[existingIndex] = { ...nextUploadingState[existingIndex], ...placeholder };
      } else {
        nextUploadingState.push(placeholder);
      }
      dispatch(updateUploadingContentState(nextUploadingState));
      uploadingContentStateRef.current = nextUploadingState;
    };

    try {
      setUploading(true);
      setProgress(0);
      setUploadError('');

      for (const [fileIndex, file] of files.entries()) {
        const { height: quality, duration } = await getVideoResolution(file);
        throwIfCancelled();
        if (quality < 480) {
          throw new Error('Please ensure that all additional angles contain videos with a minimum resolution of 480p.');
        }

        const numParts = Math.max(1, Math.ceil(file.size / PART_SIZE));
        let storedUpload = getResumeUpload(baseVideofeedId, file);
        let presignedUrls: string[] = [];

        if (!storedUpload) {
          const createResponse = await postCreateAdditionalAngleUpload({
            videofeedId: baseVideofeedId,
            name: getBaseName(file.name),
            size: file.size,
            quality,
            duration,
            numberOfParts: numParts,
          });
          const createdAt = Date.now();
          const storageKey = `${ADDITIONAL_ANGLE_RESUME_PREFIX}${baseVideofeedId}::${file.size}::${file.name}`;
          storedUpload = {
            storageKey,
            videofeedId: baseVideofeedId,
            fileName: file.name,
            size: file.size,
            quality,
            duration,
            numberOfParts: numParts,
            videoId: String(createResponse.data.videoId),
            uploadId: createResponse.data.uploadId,
            chunks: [],
            createdAt,
          };
          saveResumeUpload(storedUpload);
          setResumeUpload(storedUpload);
          presignedUrls = Array.isArray(createResponse.data.presignedUrls)
            ? [...createResponse.data.presignedUrls]
            : [];
          updatePlaceholder(file, fileIndex, storedUpload.videoId, createdAt, UploadingStatus.UPLOADING);
          onSuccess?.();
        }

        activeUploadRef.current = storedUpload;
        throwIfCancelled();
        currentUploadId = storedUpload.uploadId;
        const completedPartsByNumber = new Map(
          normalizeCompletedParts(storedUpload.chunks)
            .filter((part) => part.PartNumber <= numParts)
            .map((part) => [part.PartNumber, part] as const),
        );
        const uploadedBytesBeforeFile = uploadedBytes;
        const updateProgress = () => {
          const uploadedFileBytes = getUploadedBytesForParts(
            file.size,
            PART_SIZE,
            Array.from(completedPartsByNumber.values()),
          );
          setProgress(Math.min(99, Math.ceil(((uploadedBytesBeforeFile + uploadedFileBytes) / totalBytes) * 100)));
        };
        const missingPartNumbers = getMissingPartNumbers(numParts, Array.from(completedPartsByNumber.values()));
        let presignedRefreshPromise: Promise<void> | null = null;

        const refreshPresignedUrls = async () => {
          if (!presignedRefreshPromise) {
            const firstMissingPart = missingPartNumbers[0] || 1;
            presignedRefreshPromise = postRegeneratePresignedUrls(currentUploadId, firstMissingPart, numParts)
              .then((regenerated) => {
                (regenerated.data || []).forEach((url: string, index: number) => {
                  presignedUrls[firstMissingPart - 1 + index] = url;
                });
              })
              .finally(() => {
                presignedRefreshPromise = null;
              });
          }
          await presignedRefreshPromise;
          throwIfCancelled();
        };

        const uploadPart = async (partNumber: number) => {
          const start = (partNumber - 1) * PART_SIZE;
          const end = Math.min(start + PART_SIZE, file.size);
          const filePart = file.slice(start, end);
          let presignedUrl = presignedUrls[partNumber - 1];

          if (!presignedUrl) {
            await refreshPresignedUrls();
            presignedUrl = presignedUrls[partNumber - 1];
          }

          throwIfCancelled();
          if (!presignedUrl) {
            throw new Error('Unable to generate upload URL for an additional angle.');
          }

          const uploadedPart = await postUploadChunkByPresignedUrl(filePart, presignedUrl, uploadController.signal);
          completedPartsByNumber.set(partNumber, { PartNumber: partNumber, ETag: uploadedPart.ETag });
          const completedParts = normalizeCompletedParts(Array.from(completedPartsByNumber.values()));
          const updatedStoredUpload: AdditionalAngleResumeUpload = { ...storedUpload!, chunks: completedParts };
          storedUpload = updatedStoredUpload;
          saveResumeUpload(updatedStoredUpload);
          setResumeUpload(updatedStoredUpload);

          updateProgress();
        };

        storedUpload = { ...storedUpload, chunks: normalizeCompletedParts(Array.from(completedPartsByNumber.values())) };
        saveResumeUpload(storedUpload);
        setResumeUpload(storedUpload);
        updateProgress();

        await runWithConcurrency(missingPartNumbers, MAX_CONCURRENT_PARTS, uploadPart, () => uploadController.abort());

        throwIfCancelled();
        const completedParts = normalizeCompletedParts(Array.from(completedPartsByNumber.values())).filter(
          (part) => part.PartNumber <= numParts,
        );
        if (!hasEveryPart(numParts, completedParts)) {
          throw new Error('Additional angle upload is missing one or more parts.');
        }
        await postCompleteAdditionalAngleUpload(currentUploadId, completedParts);
        throwIfCancelled();
        uploadedBytes = uploadedBytesBeforeFile + file.size;
        localStorage.removeItem(storedUpload.storageKey);
        setResumeUpload(null);
        activeUploadRef.current = null;
        updatePlaceholder(
          file,
          fileIndex,
          storedUpload.videoId,
          storedUpload.createdAt,
          UploadingStatus.TRANSCODING,
        );
        currentUploadId = '';
      }

      setProgress(100);
      setUploadError('');
      onSuccess?.();
      onClose();
      notification.showMessage('Additional angles uploaded');
    } catch (error: any) {
      if (cancelRequestedRef.current) {
        await finishCancellation(activeUploadRef.current);
        return;
      }
      console.error(error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to upload additional angles';
      setUploadError(message);
      notification.showMessage(currentUploadId ? `${message}. Select the same file to resume.` : message);
    } finally {
      cancelRequestedRef.current = false;
      if (uploadControllerRef.current === uploadController) {
        uploadControllerRef.current = null;
      }
      setUploading(false);
    }
  };

  const removeFile = (index: number) => {
    if (uploading) {
      return;
    }
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const discardResumeUpload = async () => {
    if (!resumeUpload || uploading) return;

    try {
      setUploading(true);
      await finishCancellation(resumeUpload, false);
    } finally {
      setUploading(false);
    }
  };

  const preventDefaults = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const modal = (
    <Modal open={open && !minimized} onClose={uploading ? () => {} : onClose} loading={uploading}>
      <div className={styles.body}>
        <div className={styles.header}>
          <div className={styles.title}>Upload additional Angle/s</div>
          {minimizable && (
            <button type="button" className={styles.iconButton} onClick={() => setMinimized(true)} aria-label="Minimize upload">
              <MinimizeIcon />
            </button>
          )}
        </div>
        <div className={styles.subtitle}>
          {resumeUpload
            ? `Select ${resumeUpload.fileName} to resume the interrupted upload.`
            : remainingSlots > 0
            ? `Add up to ${remainingSlots} more angle(s). Only .mp4, .mov and .m4v files are allowed.`
            : 'This videofeed already has 4 angles.'}
        </div>

        <div
          className={`${styles.dropZone} ${isDragging ? styles.dropZoneActive : ''} ${remainingSlots === 0 && !resumeUpload ? styles.dropZoneDisabled : ''}`}
          onDragEnter={(event) => {
            preventDefaults(event);
            setIsDragging(true);
          }}
          onDragOver={preventDefaults}
          onDragLeave={(event) => {
            preventDefaults(event);
            setIsDragging(false);
          }}
          onDrop={handleDrop}
          onClick={() => {
            if (!uploading && (remainingSlots > 0 || resumeUpload)) {
              inputRef.current?.click();
            }
          }}
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            accept=".mp4,.mov,.m4v,video/mp4,video/quicktime"
            onChange={handleInputChange}
            hidden
          />
          <div className={styles.dropTitle}>
            {uploading ? 'Uploading additional angles...' : 'Drop files here or click to select'}
          </div>
          <div className={styles.dropHint}>Maximum 4 angles per videofeed.</div>
        </div>

        {files.length > 0 && (
          <div className={styles.fileList}>
            {files.map((file, index) => (
              <div key={`${file.name}-${file.size}-${index}`} className={styles.fileRow}>
                <div className={styles.fileInfo}>
                  <div className={styles.fileName}>{file.name}</div>
                  <div className={styles.fileSize}>{bytesToMB(file.size)} MB</div>
                </div>
                <button type="button" className={styles.removeButton} onClick={() => removeFile(index)} disabled={uploading}>
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}

        {(uploading || progress > 0) && (
          <div className={styles.progressWrap}>
            <LinearProgress variant="determinate" value={progress} />
          </div>
        )}

        {uploadError && <div className={styles.uploadError}>{uploadError}</div>}

        <div className={styles.actions}>
          <ContainedButton
            kind="purple"
            onClick={handleUpload}
            disabled={!files.length || uploading || (remainingSlots === 0 && !resumeUpload)}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </ContainedButton>
          <OutlinedButton
            kind="default"
            onClick={handleCancelUpload}
            disabled={cancelling || (uploading && !uploadControllerRef.current)}
          >
            {cancelling ? 'Cancelling...' : 'Cancel'}
          </OutlinedButton>
          {resumeUpload && (
            <OutlinedButton kind="default" onClick={discardResumeUpload} disabled={uploading}>
              Delete interrupted upload
            </OutlinedButton>
          )}
        </div>
      </div>
    </Modal>
  );

  return (
    <>
      {modal}
      {open && minimized && (
        <Draggable
          bounds="#root"
          onStart={() => {
            minimizedWasDraggedRef.current = false;
          }}
          onDrag={() => {
            minimizedWasDraggedRef.current = true;
          }}
        >
          <button
            type="button"
            className={styles.minimizedModal}
            onClick={() => {
              if (!minimizedWasDraggedRef.current) {
                setMinimized(false);
              }
            }}
          >
            <span className={styles.minimizedText}>
              <span className={styles.minimizedTitle}>{videofeed.title}</span>
              <span className={styles.minimizedProgress}>
                {uploading ? `Uploading additional angle... ${progress}%` : 'Upload additional Angle/s'}
              </span>
            </span>
            <MaximizeIcon className={styles.maximizeIcon} />
          </button>
        </Draggable>
      )}
    </>
  );
};

export default AdditionalAnglesUploadModal;
